import { Controller } from "@hotwired/stimulus"

/**
 * Toast notification controller for real-time notifications.
 *
 * Displays macOS-style toast notifications that slide in from the right,
 * stay briefly, and slide out automatically.
 *
 * This controller should be attached to a container element that will
 * hold the toast notifications. It listens for "notification:received"
 * custom events dispatched by the notifications controller.
 *
 * @example HTML
 *   <div data-controller="toast" class="fixed top-4 right-4 z-50 space-y-3 w-96">
 *     <!-- Toasts will be dynamically inserted here -->
 *   </div>
 */
export default class extends Controller {
  // Duration in milliseconds before auto-dismiss
  static values = {
    duration: { type: Number, default: 5000 },
    maxToasts: { type: Number, default: 5 }
  }

  /**
   * Called when controller connects to the DOM.
   * Sets up global event listener for notification events.
   */
  connect() {
    this.boundHandleNotification = this.handleNotification.bind(this)
    window.addEventListener("notification:received", this.boundHandleNotification)
  }

  /**
   * Called when controller disconnects from the DOM.
   * Cleans up event listeners.
   */
  disconnect() {
    window.removeEventListener("notification:received", this.boundHandleNotification)
  }

  /**
   * Handles incoming notification events and creates toast.
   *
   * @param {CustomEvent} event - The notification event with detail containing notification data
   */
  handleNotification(event) {
    const data = event.detail
    if (!data || !data.message) return

    this.createToast(data)
  }

  /**
   * Creates and displays a toast notification.
   *
   * @param {Object} data - The notification data
   * @param {number} data.id - The notification ID (required for navigation)
   * @param {string} data.message - The notification message
   * @param {string} data.url - Optional direct URL (used as fallback)
   * @param {string} data.icon - Optional icon name (calendar, bell, star, user-plus)
   * @param {string} data.type - Optional notification type for styling
   */
  createToast(data) {
    // Limit number of visible toasts
    const existingToasts = this.element.querySelectorAll("[data-toast]")
    if (existingToasts.length >= this.maxToastsValue) {
      // Remove oldest toast
      const oldest = existingToasts[0]
      this.dismissToast(oldest)
    }

    const toast = document.createElement("div")
    toast.setAttribute("data-toast", "")
    toast.className = this.toastClasses()
    toast.innerHTML = this.toastContent(data)

    // Add click handler for navigation
    // Navigate to /notifications/:id which marks as read and redirects
    // This matches the behavior of clicking a notification on the index page
    const notificationUrl = data.id ? `/notifications/${data.id}` : data.url
    if (notificationUrl) {
      toast.style.cursor = "pointer"
      toast.addEventListener("click", (e) => {
        if (!e.target.closest("[data-dismiss]")) {
          window.location.href = notificationUrl
        }
      })
    }

    // Add dismiss button handler
    const dismissBtn = toast.querySelector("[data-dismiss]")
    if (dismissBtn) {
      dismissBtn.addEventListener("click", (e) => {
        e.stopPropagation()
        this.dismissToast(toast)
      })
    }

    // Insert at the top
    this.element.insertBefore(toast, this.element.firstChild)

    // Trigger entrance animation (need to wait for next frame)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        toast.classList.remove("translate-x-full", "opacity-0")
        toast.classList.add("translate-x-0", "opacity-100")
      })
    })

    // Auto-dismiss after duration
    setTimeout(() => {
      this.dismissToast(toast)
    }, this.durationValue)
  }

  /**
   * Dismisses a toast with exit animation.
   *
   * @param {HTMLElement} toast - The toast element to dismiss
   */
  dismissToast(toast) {
    if (!toast || !this.element.contains(toast)) return

    // Exit animation
    toast.classList.remove("translate-x-0", "opacity-100")
    toast.classList.add("translate-x-full", "opacity-0")

    // Remove after animation
    setTimeout(() => {
      if (this.element.contains(toast)) {
        toast.remove()
      }
    }, 300)
  }

  /**
   * Returns the CSS classes for a toast element.
   *
   * @returns {string} Space-separated CSS classes
   */
  toastClasses() {
    return [
      // Base styles
      "flex", "items-start", "gap-3", "p-4", "rounded-xl", "shadow-2xl",
      // Colors - dark theme with blur
      "bg-surface-800/95", "dark:bg-surface-900/95",
      "backdrop-blur-md", "border", "border-surface-700/50",
      "text-surface-100",
      // Animation
      "transition-all", "duration-300", "ease-out",
      "translate-x-full", "opacity-0",
      // Ring for emphasis
      "ring-1", "ring-white/10"
    ].join(" ")
  }

  /**
   * Generates the HTML content for a toast.
   *
   * @param {Object} data - The notification data
   * @returns {string} HTML string for toast content
   */
  toastContent(data) {
    const icon = this.getIcon(data.icon || "bell")
    // Toast is clickable if we have an id (to go to /notifications/:id) or a direct url
    const isClickable = data.id || data.url

    return `
      <div class="flex-shrink-0 mt-0.5">
        <span class="inline-flex h-10 w-10 items-center justify-center rounded-full bg-primary-1-500/20 ring-1 ring-primary-1-500/30">
          ${icon}
        </span>
      </div>
      <div class="flex-1 min-w-0 pt-0.5">
        <p class="text-sm font-medium text-white leading-snug">${this.escapeHtml(data.message)}</p>
        ${isClickable ? '<p class="text-xs text-surface-400 mt-1">Click to view</p>' : ''}
      </div>
      <button type="button" data-dismiss class="flex-shrink-0 -mt-1 -mr-1 p-1 rounded-lg text-surface-400 hover:text-white hover:bg-surface-700/50 transition-colors">
        <svg class="h-5 w-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
        </svg>
      </button>
    `
  }

  /**
   * Gets an SVG icon for the notification type.
   *
   * @param {string} iconName - The icon name
   * @returns {string} SVG HTML
   */
  getIcon(iconName) {
    const icons = {
      calendar: `<svg class="h-5 w-5 text-primary-1-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z"></path>
      </svg>`,
      bell: `<svg class="h-5 w-5 text-primary-1-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9"></path>
      </svg>`,
      star: `<svg class="h-5 w-5 text-primary-1-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M11.049 2.927c.3-.921 1.603-.921 1.902 0l1.519 4.674a1 1 0 00.95.69h4.915c.969 0 1.371 1.24.588 1.81l-3.976 2.888a1 1 0 00-.363 1.118l1.518 4.674c.3.922-.755 1.688-1.538 1.118l-3.976-2.888a1 1 0 00-1.176 0l-3.976 2.888c-.783.57-1.838-.197-1.538-1.118l1.518-4.674a1 1 0 00-.363-1.118l-3.976-2.888c-.784-.57-.38-1.81.588-1.81h4.914a1 1 0 00.951-.69l1.519-4.674z"></path>
      </svg>`,
      "user-plus": `<svg class="h-5 w-5 text-primary-1-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M18 9v3m0 0v3m0-3h3m-3 0h-3m-2-5a4 4 0 11-8 0 4 4 0 018 0zM3 20a6 6 0 0112 0v1H3v-1z"></path>
      </svg>`
    }
    return icons[iconName] || icons.bell
  }

  /**
   * Escapes HTML to prevent XSS.
   *
   * @param {string} text - Text to escape
   * @returns {string} Escaped text
   */
  escapeHtml(text) {
    const div = document.createElement("div")
    div.textContent = text
    return div.innerHTML
  }
}
